import React, { useState } from 'react';
import { Play, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { publicAPI } from '../services/api';
import useAPI from '../hooks/useAPI';
import SEO from '../components/SEO';

// Static videos data (not managed in admin yet)
const videos = [
  {
    id: 1,
    title: "Motorhome Tour - Interior Walkthrough",
    thumbnail: "https://images.unsplash.com/photo-1693975524886-2938d6a71905",
    embedUrl: "https://www.youtube.com/embed/Qe4PqWEQeI8",
    description: "Take a full tour of our luxury motorhome interior"
  },
  {
    id: 2,
    title: "How to Drive a Motorhome - Beginner's Guide",
    thumbnail: "https://images.unsplash.com/photo-1667574307418-8a44b96d96be",
    embedUrl: "https://www.youtube.com/embed/Z5VGjHWdB8w",
    description: "Everything you need to know about driving your hired motorhome"
  },
  {
    id: 3,
    title: "Motorhome Living - Tips & Tricks",
    thumbnail: "https://images.unsplash.com/photo-1573111651692-39ec7f38fec9",
    embedUrl: "https://www.youtube.com/embed/7EEH5e3n8Gk",
    description: "Expert tips for making the most of your motorhome experience"
  }
];

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');
  const { data: galleryImages, loading, error } = useAPI(() => publicAPI.getGalleryImages(), []);

  const categories = [
    { value: 'all', label: 'All' },
    { value: 'exterior', label: 'Exteriors' },
    { value: 'interior', label: 'Interiors' },
    { value: 'lifestyle', label: 'Lifestyle' },
    { value: 'destinations', label: 'Destinations' }
  ];

  const filteredImages = (galleryImages || []).filter(img => 
    filter === 'all' ? true : img.category === filter
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <SEO 
          title="Photo & Video Gallery"
          description="Browse photos and videos of our luxury motorhomes. See interior and exterior shots of our Roller Team and Auto-Trail fleet."
          url="/gallery"
        />
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading gallery...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="Photo & Video Gallery"
        description="Browse photos and videos of our luxury motorhomes. See interior and exterior shots of our Roller Team and Auto-Trail fleet."
        keywords="motorhome photos, campervan images, motorhome interior, Roller Team gallery, Auto-Trail photos"
        url="/gallery"
      />
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Gallery</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Explore our collection of motorhome images and videos. See the luxury and comfort we offer.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <Tabs defaultValue="photos" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
            <TabsTrigger value="photos" className="text-lg">Photos</TabsTrigger>
            <TabsTrigger value="videos" className="text-lg">Videos</TabsTrigger>
          </TabsList>

          {/* Photos Tab */}
          <TabsContent value="photos">
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              {categories.map(cat => (
                <Badge
                  key={cat.value}
                  onClick={() => setFilter(cat.value)}
                  className={`cursor-pointer px-4 py-2 text-sm ${
                    filter === cat.value 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                >
                  {cat.label}
                </Badge>
              ))}
            </div>

            {/* Image Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredImages.map((image) => (
                <Dialog key={image.id}>
                  <DialogTrigger asChild>
                    <Card className="overflow-hidden cursor-pointer group hover:shadow-xl transition-all duration-300">
                      <div className="relative aspect-square overflow-hidden">
                        <img
                          src={image.url + '?w=400&h=400&fit=crop'}
                          alt={image.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-center px-4">
                            <p className="font-semibold">{image.title}</p>
                            <p className="text-sm text-slate-200">{image.motorhome}</p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl">
                    <img
                      src={image.url + '?w=1200'}
                      alt={image.title}
                      className="w-full h-auto rounded-lg"
                    />
                    <div className="mt-4">
                      <h3 className="text-xl font-bold">{image.title}</h3>
                      <p className="text-slate-600">{image.motorhome}</p>
                      <Badge className="mt-2 capitalize">{image.category}</Badge>
                    </div>
                  </DialogContent>
                </Dialog>
              ))}
            </div>

            {filteredImages.length === 0 && (
              <div className="text-center py-12">
                <p className="text-xl text-slate-600">No images found in this category.</p>
              </div>
            )}
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos">
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Motorhome Video Tours & Guides</h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Watch our detailed video tours and helpful guides to get a better feel for our motorhomes and what to expect.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video) => (
                <Card key={video.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="relative aspect-video bg-slate-900 group cursor-pointer">
                    <img
                      src={video.thumbnail + '?w=600&h=400&fit=crop'}
                      alt={video.title}
                      className="w-full h-full object-cover opacity-75 group-hover:opacity-50 transition-opacity"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 transition-all hover:scale-110">
                            <Play className="h-8 w-8 fill-current" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl">
                          <div className="aspect-video">
                            <iframe
                              width="100%"
                              height="100%"
                              src={video.embedUrl}
                              title={video.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="rounded-lg"
                            ></iframe>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-2">{video.title}</h3>
                    <p className="text-slate-600 text-sm">{video.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* CTA Section */}
      <section className="bg-blue-600 text-white py-16 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Create Your Own Memories?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Book one of our motorhomes and start your adventure today!
          </p>
          <a
            href="mailto:milliesmotorhomehire@gmail.com"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
